import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Axios from "@/shared/api/axios";
import Loading from "@/components/ui/Loading";
import StatusModal from "@/components/ui/StatusModal";
import Pagination from "@/components/ui/Pagination";
import WorldCard from "@/components/ui/WorldCard";

const LIMIT = 12;

const TabWorld = ({ viewIdolId }) => {
  const { t } = useTranslation();

  const idolId = useSelector((state) => state.user.idolId);

  const statusModalRef = useRef(null);
  const shouldScrollOnLoad = useRef(false);

  const [isLoading, setIsLoading] = useState(false);
  const [list, setList] = useState(null);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentStatusCode, setCurrentStatusCode] = useState('');

  const getList = async (page) => {
    setIsLoading(true);
    try {
      const res = await Axios.post("/idol/get-link-world-list", {
        idol_id: idolId,
        view_idol_id: viewIdolId,
        page,
        limit: LIMIT,
      });
      switch (res.data.status_code) {
        case "SYSTEM_1000":
          setList(
            res.data.data.map((item) => ({
              ...item,
              world_cover_url: item.world_media_url,
            }))
          );
          setTotal(res.data.total);
          break;
        default:
          setCurrentStatusCode(res.data.status_code);
          statusModalRef.current?.show();
          break;
      }
    } catch (error) {
      // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
      if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
        setCurrentStatusCode(error.httpErrorCode);
        statusModalRef.current?.show();
      } else if (!error.httpErrorCode) {
        console.error('Get followed world list error:', error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (viewIdolId) {
      setCurrentPage(1);
      shouldScrollOnLoad.current = true;
      getList(1);
    }
  }, [viewIdolId]);

  // 切到此 tab 當下資料還沒回來，內容比 navbar 下方預期位置短，此時捲動會被瀏覽器 clamp 回頂端；
  // 要等資料進來、grid 實際撐開高度後才能正確捲到 tab 列（分頁換頁不需要，故用 ref 只在初次載入時觸發一次）
  useEffect(() => {
    if (list !== null && shouldScrollOnLoad.current) {
      shouldScrollOnLoad.current = false;
      document.querySelector('.idol-tab-bar')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [list]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    getList(page);
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <>
      <div className="bg-white border rounded shadowA px-3 py-4 mb-4">
        {list === null ? (
          <div className="idol-tab-loading-placeholder" />
        ) : list.length > 0 ? (
          <div className="cards-grid">
            {list.map((item) => (
              <WorldCard key={item.world_id} world={item} />
            ))}
          </div>
        ) : (
          <p className="mb-0 text-center">{t("no_followed_world")}</p>
        )}

        {totalPages > 1 && (
          <div className="d-flex justify-content-center mt-3">
            <Pagination
              total={total}
              limit={LIMIT}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              scrollMode="container"
              scrollContainerSelector=".idol-tab-bar"
            />
          </div>
        )}
      </div>

      <StatusModal ref={statusModalRef} statusCode={currentStatusCode} />
      {isLoading && <Loading />}
    </>
  );
};

export default TabWorld;
